import type { Dispatch, SetStateAction } from 'react';
import type { Exercise } from '@heraion/shared';

import type { DurableWriteFn } from '../seams';
import { learnedRest } from './state/learnedRestStore';

// The one write of a per-exercise timer override (rest + countdown/hold
// default) and the reconcile of the in-memory catalog. Its three callers (the
// Settings card on mobile and web, the in-logger timer sheet useTimerDefaults,
// the rest-banner gear useRestPicker) share ONE copy instead of each
// hand-rolling the same request + catalog patch (§CONSOLIDAMENTO). Store only
// the fields that DIFFER from the global default; a value equal to the global
// is cleared so the exercise falls back to the global instead of carrying a
// redundant override.
//
// #411: the timer is the athlete's. A stock exercise's goes to the athlete's
// own row, PUT/DELETE /api/me/exercise-timers/<slug>, never to the shared
// catalog row (the old `?tier=stock` PATCH set it for every athlete). The
// exercise list lays those rows back over the catalog. An exercise of the
// athlete's own (source user / auto_imported) already lives in their
// partition, so its timer stays a PATCH of that row, as before.
// Throws on a rejected write, and when the exercise is not in the catalog:
// the caller owns the saving/error UI.
export async function saveExerciseTimerDefaults({
  slug,
  restSec,
  holdSec,
  restGlobal,
  holdGlobal,
  catalog,
  setCatalog,
  durableWrite,
  onSaved,
}: {
  slug: string;
  restSec: number;
  /** Undefined = leave the hold as it is (the rest-banner gear sets the rest
   *  only). */
  holdSec?: number;
  restGlobal: number;
  /** Needed only with a holdSec. */
  holdGlobal?: number;
  catalog: Exercise[] | null;
  setCatalog: Dispatch<SetStateAction<Exercise[] | null>>;
  /** RW-18: mobile passes its outbox-backed durableWrite; web-rnw its
   *  send-only equivalent (packages/ui/src/seams.ts). */
  durableWrite: DurableWriteFn;
  /** Marks the host's catalog source stale (#342), so the next read
   *  revalidates instead of serving the cached rows. Optional. */
  onSaved?: () => void;
}): Promise<void> {
  const ex = catalog?.find((x) => x.slug === slug);
  // Not a silent success: the caller's error path tells the athlete.
  if (!ex) throw new Error(`exercise_not_in_catalog: ${slug}`);
  const restNext = restSec === restGlobal ? null : restSec;
  const holdNext =
    holdSec === undefined ? undefined : holdSec === holdGlobal ? null : holdSec;
  // Queued counts as saved (DX-2/E): the local catalog edit stands either
  // way, and a rejected write throws so the caller's own handler runs.
  const result = await durableWrite(timerRequest(ex, restNext, holdNext));
  if (result.outcome === 'rejected') throw result.error;
  setCatalog((prev) =>
    prev
      ? prev.map((c) =>
          c.slug === slug
            ? {
                ...c,
                defaultRestSec: restNext ?? undefined,
                defaultDurationSec:
                  holdNext === undefined
                    ? c.defaultDurationSec
                    : (holdNext ?? undefined),
              }
            : c,
        )
      : null,
  );
  // The athlete just chose this rest: it outranks whatever was learned.
  learnedRest.clear(slug);
  onSaved?.();
}

/** The request that stores an exercise's timers. `null` clears a value;
 *  `holdNext` undefined leaves the hold as the exercise has it. Pure. */
export function timerRequest(
  ex: Exercise,
  restNext: number | null,
  holdNext: number | null | undefined,
): Parameters<DurableWriteFn>[0] {
  const path = encodeURIComponent(ex.slug);
  if (ex.source !== 'stock') {
    // The athlete's own exercise: its row is already theirs.
    const body: Record<string, number | null> = { defaultRestSec: restNext };
    if (holdNext !== undefined) body['defaultDurationSec'] = holdNext;
    return { method: 'PATCH', path: `/api/exercises/${path}`, body };
  }
  // A stock exercise: the athlete's row. A PUT is the whole row, so a hold
  // left alone is sent as the exercise has it now.
  const hold = holdNext === undefined ? (ex.defaultDurationSec ?? null) : holdNext;
  if (restNext === null && hold === null)
    return { method: 'DELETE', path: `/api/me/exercise-timers/${path}` };
  const body: { restSec?: number; holdSec?: number } = {};
  if (restNext !== null) body.restSec = restNext;
  if (hold !== null) body.holdSec = hold;
  return { method: 'PUT', path: `/api/me/exercise-timers/${path}`, body };
}
